import React, { useContext, useState } from 'react';

import { Radio, RadioGroup, FormControlLabel, FormControl, Skeleton } from '@mui/material';
import moment, { Moment } from 'moment';
import { Trans, useTranslation } from 'react-i18next';

import { BreadcrumbItemProps } from '../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import CardNE from '../../../shared/components/CardNE/CardNE';
import CardStatusNE from '../../../shared/components/CardStatusNE/CardStatusNE';
import CustomEmptyPage from '../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import DatePicker from '../../../shared/components/DatePicker/DatePicker';
import DropzoneFileUploader from '../../../shared/components/DropzoneFileUploader/DropzoneFileUploader';
import PageCurvedContainer from '../../../shared/components/PageCurvedContainer/PageCurvedContainer';
import StyledButtonNE from '../../../shared/components/StyledButtonNE/StyledButtonNE';
import TextField from '../../../shared/components/TextField/TextField';
import { ONLY_NUMBERS_REGEX } from '../../../shared/constants/constants';
import { REWARDS_VOUCHER_UPLOADER_VALIDATORS } from '../../../shared/constants/rewards';
import {
  CLICK_ACTION,
  CONFIRM_LOAD,
  MORE_LOAD,
  PAYMENT_TYPE_BANK_TRANSFER,
  PAYMENT_TYPE_CREDIT_LINE
} from '../../../shared/constants/tagManager';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { RewardsContext } from '../../../shared/context/rewards/rewardsContext';
import { ILoadBalanceData } from '../../../shared/interfaces/rewards';
import { Rewards } from '../../../shared/services/api/rewards/rewards';
import tagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_LOAD_BALANCE } from '../../../shared/translations/rewards';
import { TRANSLATIONS_SETTINGS } from '../../../shared/translations/settings';
import { RewardsBalancePaymentMethods } from '../../../shared/types/rewards';
import { toLocaleNumberFormat } from '../../../shared/utils/utils';
import RewardsPageHeader from '../sections/RewardsPageHeader/RewardsPageHeader';
import LoadBalanceError from './sections/Error/LoadBalanceError';
import LoadBalanceSuccess from './sections/Success/LoadBalanceSuccess';

import './RewardsLoadBalance.scss';

const RewardsLoadBalance = () => {
  const [depositAmount, setDepositAmount] = useState<string>('');
  const [isValidAmount, setIsValidAmount] = useState<boolean | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<number | null>(null);
  const [voucherFile, setVoucherFile] = useState<string>('');
  const [voucherFilename, setVoucherFilename] = useState<string>('');

  const [successfulBalanceLoad, setSuccessfulBalanceLoad] = useState<boolean | null>(null);
  const [orderLabel, setOrderLabel] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [orderNumber, setOrderNumber] = useState<string>('');
  const [hes, setHes] = useState('');
  const [hesDate, setHesDate] = useState<Moment | null>(null);

  const today = (): Moment => moment();
  const in1Year = (): Moment => moment().add(1, 'year');

  const { company, isMexico } = useContext(CompanyContext);
  const {
    balance,
    hasCreditLine,
    transferData,
    currency,
    internalProducts,
    loading: rewardsLoading,
    error: rewardsError,
    countryCode
  } = useContext(RewardsContext);
  const { t } = useTranslation();

  const loadBalanceBreadcrumbs: BreadcrumbItemProps[] = [
    { label: t(TRANSLATION_LOAD_BALANCE.header.breadcrumb.rewards) },
    { label: t(TRANSLATION_LOAD_BALANCE.header.breadcrumb.loadBalance) }
  ];

  const handleOnChangePaymentMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setPaymentMethod(value);
  };

  const handleOnChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (!RegExp(ONLY_NUMBERS_REGEX).exec(value)) {
      return;
    }
    const isValid = checkIfAmountIsValid(value);
    if (isValid) {
      value = limitMaxAmount(value);
    }
    setDepositAmount(value);
    setIsValidAmount(isValid);
  };

  const handleOnChangeHes = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHes(e.target.value);
  };

  const handleOnChangeOrderNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderNumber(e.target.value);
  };

  const limitMaxAmount = (value: string) => {
    return value.length > 13 ? value.slice(0, 13) : value;
  };

  const checkIfAmountIsValid = (amount: string) => {
    if (!amount) {
      return false;
    }
    return Number(amount) > 0;
  };

  const loadBalance = async () => {
    if (!readyToChargeBalance) {
      return;
    }

    const balanceProduct = internalProducts.find((product) => product.type === 'balance');
    if (!balanceProduct) {
      return;
    }

    try {
      setLoading(true);
      const loadBalanceData: ILoadBalanceData = {
        countryCode: countryCode,
        orderAmount: depositAmount !== '' ? Number(depositAmount) : 0,
        paymentDetail: {
          paymentMethod: RewardsBalancePaymentMethods[paymentMethod!],
          currency: currency
        },
        productId: balanceProduct.id,
        productName: balanceProduct.name
      };
      if (paymentMethod === RewardsBalancePaymentMethods['bank-transfer']) {
        loadBalanceData.paymentVoucher = {
          base64: voucherFile ?? '',
          fileName: voucherFilename ?? ''
        };
      }
      if (paymentMethod === RewardsBalancePaymentMethods['credit-line']) {
        loadBalanceData.purchaseOrder = {
          base64: voucherFile,
          fileName: voucherFilename,
          code: orderNumber,
          hesCode: hes,
          hesDate: hesDate ? hesDate.format() : ''
        };
      }
      const response = await Rewards.loadBalance(company.id, loadBalanceData);
      const gtmParams =
        RewardsBalancePaymentMethods[paymentMethod!] === 'bank-transfer'
          ? PAYMENT_TYPE_BANK_TRANSFER
          : PAYMENT_TYPE_CREDIT_LINE;
      tagManagerService.pushEvent(CLICK_ACTION, CONFIRM_LOAD, gtmParams);
      setOrderLabel(response.orderLabel);
      setSuccessfulBalanceLoad(true);
    } catch {
      setSuccessfulBalanceLoad(false);
    } finally {
      setLoading(false);
    }
  };

  const updateBase64 = (fileBase64: string) => {
    setVoucherFile(fileBase64);
  };

  const saveFileName = (fileName: string) => {
    setVoucherFilename(fileName);
  };

  const restartLoadBalance = () => {
    setSuccessfulBalanceLoad(null);
    setDepositAmount('');
    setIsValidAmount(null);
    setPaymentMethod(null);
    setVoucherFile('');
    setVoucherFilename('');
    tagManagerService.pushEvent(CLICK_ACTION, MORE_LOAD);
  };

  const readyToChargeBalance = () => {
    if (!isValidAmount || paymentMethod === null) {
      return false;
    }
    if (paymentMethod === RewardsBalancePaymentMethods['bank-transfer'] && voucherFile === '') {
      return false;
    }
    if (paymentMethod === RewardsBalancePaymentMethods['credit-line'] && (!orderNumber || !voucherFile)) {
      return false;
    }
    return true;
  };

  if (successfulBalanceLoad) {
    return (
      <LoadBalanceSuccess
        breadcrumbs={loadBalanceBreadcrumbs}
        loadMoreBalance={restartLoadBalance}
        orderLabel={orderLabel}
        paymentMethod={paymentMethod ?? RewardsBalancePaymentMethods['bank-transfer']}
      />
    );
  }
  if (successfulBalanceLoad === false) {
    return <LoadBalanceError breadcrumbs={loadBalanceBreadcrumbs} retry={loadBalance} cancel={restartLoadBalance} />;
  }

  return (
    <div className="page-container curved" data-experience="new">
      <RewardsPageHeader
        breadcrumbs={loadBalanceBreadcrumbs}
        rewardsLoading={rewardsLoading}
        rewardsError={rewardsError}
        currency={currency}
        balance={balance}
        title={t(TRANSLATION_LOAD_BALANCE.header.title)}
        description={t(TRANSLATION_LOAD_BALANCE.header.description)}
        button={false}
      />
      <PageCurvedContainer>
        {rewardsError ? (
          <CustomEmptyPage
            title={t(TRANSLATIONS_SETTINGS.error.title)}
            subtitle={t(TRANSLATIONS_SETTINGS.error.subtitle)}
            newExperience={true}
          />
        ) : (
          <>
            <CardNE className="justify-between gap-4">
              <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_LOAD_BALANCE.cards.title1)}</h3>
              <CardStatusNE type="informative" message={t(TRANSLATION_LOAD_BALANCE.cards.statusCard1)} />
              <TextField
                error={isValidAmount === false}
                value={depositAmount}
                onChange={handleOnChangeAmount}
                data-testid="domain-input"
                name="es"
                type="number"
                label={t(TRANSLATION_LOAD_BALANCE.cards.input)}
                id="orderAmount"
              />
            </CardNE>

            <CardNE className="justify-between gap-4">
              <div className="load-balance-payment-title">
                <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_LOAD_BALANCE.cards.title2)}</h3>
                {!rewardsLoading && !hasCreditLine && !isMexico && (
                  <div className="credit-line-information">
                    <Trans
                      i18nKey={TRANSLATION_LOAD_BALANCE.cards.creditInformation}
                      components={{
                        u: (
                          <a
                            className="text-link"
                            href="https://intercom.help/gobetterfly/es/articles/10030114-solicitud-de-linea-de-credito"
                            target="_blank"
                            rel="noopener noreferrer"
                          ></a>
                        )
                      }}
                    />
                  </div>
                )}
              </div>
              <div>
                <p className="ne-font-body-sm m-0">{t(TRANSLATION_LOAD_BALANCE.cards.total)}</p>
                <p className="ne-font-numeric-md m-0">
                  {isValidAmount && depositAmount !== ''
                    ? toLocaleNumberFormat(parseFloat(depositAmount), { style: 'currency', currency: currency })
                    : toLocaleNumberFormat(0, { style: 'currency', currency: currency })}
                </p>
              </div>

              {rewardsLoading ? (
                <Skeleton variant="rectangular" width={400} height={30} />
              ) : (
                <FormControl variant="standard">
                  <RadioGroup
                    row
                    value={paymentMethod}
                    name="radio-buttons-group"
                    role="payment-method-selector"
                    onChange={handleOnChangePaymentMethod}
                  >
                    <FormControlLabel
                      value={RewardsBalancePaymentMethods['bank-transfer']}
                      control={<Radio color="primary" />}
                      label={t(TRANSLATION_LOAD_BALANCE.cards.radioButton1)}
                      labelPlacement="end"
                    />
                    {hasCreditLine && (
                      <FormControlLabel
                        value={RewardsBalancePaymentMethods['credit-line']}
                        control={<Radio color="primary" />}
                        label={t(TRANSLATION_LOAD_BALANCE.cards.radioButton2)}
                        labelPlacement="end"
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              )}

              {paymentMethod === RewardsBalancePaymentMethods['bank-transfer'] && (
                <div id="transfer-payment-file" className="flex flex-row justify-between align-start gap-8">
                  <div className="flex flex-column gap-4 grow-2">
                    <h4 className="ne-font-body-md font-bold m-0">
                      {t(TRANSLATION_LOAD_BALANCE.cards.transferTitle1)}
                    </h4>
                    <DropzoneFileUploader
                      updateBase64={(base64File) => updateBase64(base64File)}
                      saveFileName={(fileName) => saveFileName(fileName)}
                      validator={REWARDS_VOUCHER_UPLOADER_VALIDATORS}
                      inputTitle={t(TRANSLATION_LOAD_BALANCE.cards.transferFileTitle)}
                    />
                  </div>
                  <div className="flex flex-column gap-4 grow-1">
                    <h4 className="ne-font-body-md font-bold m-0">
                      {t(TRANSLATION_LOAD_BALANCE.cards.transferTitle2)}
                    </h4>
                    <div className="transfer-data-card">
                      {transferData.name && (
                        <p className="ne-font-body-md m-0">
                          <b>{t(TRANSLATION_LOAD_BALANCE.cards.transferData.name)}:</b> {transferData.name}
                        </p>
                      )}
                      {transferData.id && (
                        <p className="ne-font-body-md m-0">
                          <b>{transferData.idType}:</b> {transferData.id}
                        </p>
                      )}
                      {transferData.accountNumber && (
                        <p className="ne-font-body-md m-0">
                          <b>{t(TRANSLATION_LOAD_BALANCE.cards.transferData.type)}:</b> {transferData.accountType}
                        </p>
                      )}
                      {transferData.accountNumber && (
                        <p className="ne-font-body-md m-0">
                          <b>{t(TRANSLATION_LOAD_BALANCE.cards.transferData.account)}:</b> {transferData.accountNumber}
                        </p>
                      )}
                      {transferData.email && (
                        <p className="ne-font-body-md m-0">
                          <b>{t(TRANSLATION_LOAD_BALANCE.cards.transferData.email)}:</b> {transferData.email}
                        </p>
                      )}
                      {transferData.clabe && (
                        <p className="ne-font-body-md m-0">
                          <b>CLABE:</b> {transferData.clabe}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {paymentMethod === RewardsBalancePaymentMethods['credit-line'] && (
                <div>
                  <CardStatusNE type="informative" message={t(TRANSLATION_LOAD_BALANCE.cards.statusCard2)} />
                  <h4 className="ne-font-body-md font-bold my-4">{t(TRANSLATION_LOAD_BALANCE.cards.creditTitle1)}</h4>
                  <div id="credit-payment-file" className="flex flex-row justify-between items-center gap-4">
                    <div className="flex flex-column grow-1">
                      <TextField
                        variant="standard"
                        className="custom-input"
                        label={t(TRANSLATION_LOAD_BALANCE.cards.purchaseOrder.number)}
                        type="text"
                        value={orderNumber}
                        onChange={handleOnChangeOrderNumber}
                        id="purchaseOrderNumber"
                      />
                      <TextField
                        variant="standard"
                        className="custom-input"
                        label={t(TRANSLATION_LOAD_BALANCE.cards.purchaseOrder.codeHes)}
                        type="text"
                        value={hes}
                        onChange={handleOnChangeHes}
                        id="purchaseOrderHes"
                      />
                      <DatePicker
                        label={t(TRANSLATION_LOAD_BALANCE.cards.purchaseOrder.dateHes)}
                        value={hesDate ?? null}
                        minDate={today()}
                        maxDate={in1Year()}
                        onChange={(date) => setHesDate(date)}
                        className="w-full"
                        id="purchaseOrderDateHes"
                      />
                    </div>
                    <div className="flex flex-column gap-4 grow-2 max-w-3xl">
                      <DropzoneFileUploader
                        updateBase64={(base64File) => updateBase64(base64File)}
                        saveFileName={(fileName) => saveFileName(fileName)}
                        validator={REWARDS_VOUCHER_UPLOADER_VALIDATORS}
                        inputTitle={t(TRANSLATION_LOAD_BALANCE.cards.creditFileTitle)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardNE>
            <div className="flex justify-end">
              <StyledButtonNE
                buttonType="primary"
                buttonSize="lg"
                classes="w-auto"
                action={loadBalance}
                loading={loading}
                disabled={!readyToChargeBalance()}
              >
                {t(TRANSLATION_LOAD_BALANCE.button)}
              </StyledButtonNE>
            </div>
          </>
        )}
      </PageCurvedContainer>
    </div>
  );
};

export default RewardsLoadBalance;
